import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { find, get, uniqBy } from "lodash";

import Loading from "../../../../components/Loading/Loading";
import HowMany from "../RICBTEHowMany/HowMany";
import { CartStatus, OrderBuilderStatus } from "../../store/constants";
import { OrderStatus } from "../../../storeHome/store/constants";

import(/* webpackChunkName: "order-builder-model" */ "../RICBTEModel/model.scss");
import(/* webpackChunkName: "order-builder-implant" */ "./implantItemBuilder.scss");

const ImplantItemBuilder = ({
  cartData,
  cartStatus,
  addOrderToCart,
  orderBuilderStatus,
  orderBuilderData,
  setIsStockOrder,
}) => {
  const history = useHistory();
  const items = get(orderBuilderData, "Items", []);
  const models = get(orderBuilderData, "Models", []);
  const [selectedModelCode, setSelectedModelCode] = useState("");
  const [leftOrRight, setLeftOrRight] = useState("");
  const [submittedItems, setSubmittedItems] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);

  const availableModels = useMemo(() => {
    const itemModelCodes = items
      .map((item) => item.ModelTypeCode)
      .filter((code) => code);

    const modelsForItems = models.filter((model) =>
      itemModelCodes.includes(model.Code)
    );

    return uniqBy(modelsForItems, "Code");
  }, [items, models]);

  const selectedModelItems = useMemo(
    () =>
      items.filter((item) => item.ModelTypeCode === selectedModelCode),
    [items, selectedModelCode]
  );

  const getSideItem = (side) => {
    const codeSuffix = side === "R" ? "-R" : "-L";
    const sideName = side === "R" ? "right" : "left";

    return selectedModelItems.find((item) => {
      const code = (item.Code || "").toUpperCase();
      const name = (item.Name || "").toLowerCase();
      return code.endsWith(codeSuffix) || name.includes(sideName);
    });
  };

  const selectedSides =
    leftOrRight === "B" ? ["R", "L"] : leftOrRight ? [leftOrRight] : [];

  useEffect(() => {
    if (cartStatus === CartStatus.SUCCESS && submittedItems.length > 0) {
      history.push({
        pathname: `/related-products/${submittedItems[0].Code}`,
        state: { productsAddedToCart: submittedItems },
      });
    }
  }, [cartStatus, history, submittedItems]);

  const addImplantToOrder = () => {
    const validationErrors = [];

    if (!selectedModelCode) {
      validationErrors.push("Please select an implant model");
    }

    if (!leftOrRight) {
      validationErrors.push("Please select at least one side");
    }

    const itemsForCart = selectedSides
      .map((side) => {
        const sideItem = getSideItem(side);
        if (!sideItem) {
          validationErrors.push(
            `The selected model does not have a ${
              side === "R" ? "Right" : "Left"
            } item configured`
          );
          return null;
        }

        return {
          ...sideItem,
          ItemCode: sideItem.Code,
          Quantity: 1,
          Side: side,
          Type: "Standard",
        };
      })
      .filter((item) => item);

    setErrorMessages(validationErrors);
    if (validationErrors.length > 0) return;

    const currentCart = cartData ? [...cartData] : [];
    itemsForCart.forEach((itemForCart) => {
      const existingIndex = currentCart.findIndex(
        (item) =>
          item.Code === itemForCart.Code && item.Side === itemForCart.Side
      );

      if (existingIndex >= 0) {
        currentCart[existingIndex] = {
          ...currentCart[existingIndex],
          Quantity: Number(currentCart[existingIndex].Quantity || 0) + 1,
        };
      } else {
        currentCart.push(itemForCart);
      }
    });

    setSubmittedItems(itemsForCart);
    setIsStockOrder(OrderStatus.REGULAR);
    addOrderToCart(currentCart);
  };

  return (
    <div className="order-builder-main-div implant-item-builder-main-div">
      {orderBuilderStatus === OrderBuilderStatus.LOADING && <Loading />}

      {orderBuilderStatus === OrderBuilderStatus.ERROR && (
        <div className="alert alert-danger" role="alert">
          The implant products could not be loaded. Please try again.
        </div>
      )}

      {orderBuilderStatus === OrderBuilderStatus.SUCCESS && (
        <div>
          <h1>Implant</h1>
          {availableModels.length === 0 ? (
            <div className="alert alert-info" role="alert">
              There are currently no implant models available to order.
            </div>
          ) : (
            <div>
              <section className="model-component-main-div order-builder-component implant-product-section">
                <h2 className="order-builder-component-title">
                  Choose a model
                </h2>
                <Row>
                  {availableModels.map((model) => {
                    const isSelected = selectedModelCode === model.Code;
                    const modelItem = find(items, {
                      ModelTypeCode: model.Code,
                    });
                    const imagePath = model.ImagePath || modelItem?.ImagePath;

                    return (
                      <Col md={6} lg={3} className="border-right" key={model.Code}>
                        <button
                          className="implant-model-button"
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => {
                            setSelectedModelCode(model.Code);
                            setErrorMessages([]);
                          }}
                        >
                          <div className={`model-box ${isSelected ? "active" : ""}`}>
                            <div className="model-box-image">
                              {imagePath && (
                                <img
                                  src={imagePath}
                                  className="img-fluid"
                                  alt=""
                                />
                              )}
                            </div>
                            <div className="model-box-text">
                              <p>{model.Name}</p>
                            </div>
                          </div>
                        </button>
                      </Col>
                    );
                  })}
                </Row>
              </section>

              <HowMany
                howManyForOrder={leftOrRight === "B" ? 2 : 1}
                setHowManyForOrder={() => {}}
                leftorRight={leftOrRight}
                setLeftOrRight={setLeftOrRight}
              />

              {errorMessages.length > 0 && (
                <Row className="order-builder-component-title">
                  <Col xs={12} className="alert alert-danger">
                    <p>Please resolve the following errors</p>
                    <ul>
                      {errorMessages.map((message) => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  </Col>
                </Row>
              )}

              <div className="item-builder-add-to-cart-button">
                <button
                  className="btn float-right"
                  type="button"
                  disabled={cartStatus === CartStatus.LOADING}
                  onClick={addImplantToOrder}
                >
                  {cartStatus === CartStatus.LOADING
                    ? "Adding to order..."
                    : "Add to order"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ImplantItemBuilder.propTypes = {
  cartData: PropTypes.array,
  cartStatus: PropTypes.string,
  addOrderToCart: PropTypes.func,
  orderBuilderStatus: PropTypes.string,
  orderBuilderData: PropTypes.object,
  setIsStockOrder: PropTypes.func,
};

export default ImplantItemBuilder;
